import type {
  ResolvedKbAdherenceThemeFinding,
  ResolvedKbAdherenceThemesData,
} from "@repo/app-types/entities/kbAdherenceThemes";
import { KnowledgeBaseAdherenceCorrectness } from "@repo/app-types/entities/knowledgeBaseAdherence";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { UNKNOWN_CONTACT_DISPLAY_NAME } from "@repo/app-types/helpers/utilities";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { IconAlertTriangle, IconChevronDown } from "@tabler/icons-react";

import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  GridList,
  GridListItem,
  Heading,
  type Key,
} from "react-aria-components";
import { useNavigate } from "react-router";
import { z } from "zod";
import { usePlaybookTeammateMostRecentKbAdherenceThemesQuery } from "../../api/entities/performancePlaybook.js";
import { KnowledgeBaseAdherenceCorrectnessIcon } from "./knowledgeBaseAdherenceCorrectnessIcon.js";

const MIN_CALL_PERCENTAGE = 5;

interface KnowledgeBaseAdherenceThemesCardProps {
  playbookId: PlaybookId;
  teammateId: TeammateId;
}

export const KnowledgeBaseAdherenceThemesCard: FC<
  KnowledgeBaseAdherenceThemesCardProps
> = ({ playbookId, teammateId }) => {
  const navigate = useNavigate();

  const { data, isLoading } =
    usePlaybookTeammateMostRecentKbAdherenceThemesQuery({
      playbookId,
      teammateId,
    });

  const filteredThemes = useMemo(() => filterThemes(data), [data]);

  const handleFindingAction = (
    findings: readonly ResolvedKbAdherenceThemeFinding[],
    key: Key,
  ) => {
    const finding = findings.find(
      (f) => `${f.account_id}-${f.statement_index}` === key,
    );
    if (!finding) {
      return;
    }

    // The backend resolver always supplies grading_id
    // (resolveKbAdherenceThemes.ts: `findingRef.grading_id ?? grading.id`, and
    // findings whose grading can't be resolved are dropped), so even legacy
    // themes reach us with one. The tuple branch is a defensive fallback only.
    const statementSubPath = `assessment/knowledge-base-adherence/${encodeURIComponent(String(finding.statement_index))}`;
    const target = finding.grading_id
      ? `customer-interactions/${encodeURIComponent(finding.grading_id)}/${statementSubPath}`
      : `accounts/${encodeURIComponent(finding.account_id)}/${statementSubPath}`;
    navigate(target, { state: { isModal: true, canGoBack: true } });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader style={LoaderStyle.SPINNER} />
      </div>
    );
  }

  if (filteredThemes.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {filteredThemes.map((theme, themeIndex) => (
        <Disclosure
          key={themeIndex}
          className="rounded-lg border border-border-light bg-white transition-colors hover:border-orange-300 data-[expanded]:border-orange-300"
        >
          <Heading>
            <AriaButton
              slot="trigger"
              className="group w-full min-w-0 cursor-pointer text-left"
            >
              <div className="flex items-center gap-3 px-3 py-2.5">
                <IconAlertTriangle className="size-4 shrink-0 text-orange-500" />
                <div className="text-xs font-medium text-muted-foreground tabular-nums">
                  {Math.round(theme.call_percentage)}% of calls
                </div>
                <div className="line-clamp-2 min-w-0 flex-1 text-sm leading-snug text-foreground group-aria-expanded:line-clamp-none">
                  {theme.statement}
                </div>
                <IconChevronDown className="size-4 shrink-0 text-gray-400 transition-transform group-aria-expanded:rotate-180" />
              </div>
            </AriaButton>
          </Heading>

          <DisclosurePanel>
            <div className="border-t border-border-light">
              <GridList
                aria-label={`Findings for ${theme.statement}`}
                className="grid grid-cols-[12rem_minmax(0,1fr)] divide-y divide-gray-100"
                items={theme.findings.map((f) => ({
                  ...f,
                  id: `${f.account_id}-${f.statement_index}`,
                }))}
                onAction={(key) => handleFindingAction(theme.findings, key)}
              >
                {(finding) => (
                  <GridListItem
                    id={finding.id}
                    className="col-span-full grid cursor-pointer grid-cols-subgrid items-start gap-3 px-3 py-2 outline-hidden hover:bg-blue-50"
                    textValue={finding.summary}
                  >
                    <div className="text-sm">
                      <div className="truncate font-medium">
                        {finding.contact_primary_display ??
                          finding.contact_secondary_display ??
                          UNKNOWN_CONTACT_DISPLAY_NAME}
                      </div>
                      {finding.contact_primary_display &&
                        finding.contact_secondary_display &&
                        !isUuid(finding.contact_secondary_display) && (
                          <div className="truncate text-xs text-muted-foreground">
                            {finding.contact_secondary_display}
                          </div>
                        )}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <span>{formatDate(finding.last_recorded_at)}</span>
                        <span className="text-gray-400">&middot;</span>
                        <span className="text-gray-600">
                          {formatCallInfo(
                            finding.recording_count,
                            finding.total_duration_seconds,
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-foreground">
                      <KnowledgeBaseAdherenceCorrectnessIcon
                        correctness={
                          KnowledgeBaseAdherenceCorrectness.Incorrect
                        }
                      />
                      {finding.summary}
                    </div>
                  </GridListItem>
                )}
              </GridList>
            </div>
          </DisclosurePanel>
        </Disclosure>
      ))}
    </div>
  );
};

function filterThemes(data: ResolvedKbAdherenceThemesData | null | undefined) {
  if (!data) {
    return [];
  }

  return data.themes
    .filter((theme) => theme.call_percentage >= MIN_CALL_PERCENTAGE)
    .toSorted((a, b) => b.call_percentage - a.call_percentage);
}

function formatDate(isoDate: string): string {
  const dt = DateTime.fromISO(isoDate);
  const now = DateTime.now();

  if (dt.hasSame(now, "day")) {
    return dt.toLocaleString(DateTime.TIME_SIMPLE);
  }

  return dt.toLocaleString({ month: "short", day: "numeric" });
}

const uuidSchema = z.uuid();

function isUuid(value: string): boolean {
  return uuidSchema.safeParse(value).success;
}

function formatCallInfo(
  recordingCount: number,
  totalDurationSeconds: number,
): string {
  const callLabel = recordingCount === 1 ? "1 call" : `${recordingCount} calls`;
  const minutes = Math.round(totalDurationSeconds / 60);
  return `${callLabel} (${minutes}m)`;
}
